import {
	AlertModal,
	ContentState,
	MainFooter,
	MainHeader,
	StepProgressBar,
	StyledGrid,
	VoiceTranscriptionDrawer,
} from 'presentation/components';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {Box, Button, Stack, Typography, useTheme} from '@mui/material';
import {
	FeaturesEnum,
	HomeEnum,
	TelehealthBookingEnum,
	TermsOriginEnum,
} from 'presentation/constants';
import {
	useGetEndpointUrl,
	useHealthEmergencyLinkFactory,
	usePersonalData,
} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import Icon from 'presentation/icons';
import {useVoiceTranscriptionDrawerStore} from 'presentation/components/VoiceTranscriptionDrawer/store';
import {useMediaQueries} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {handleServiceTypeConfigs} from 'presentation/pages/private/booking/utils/handleServiceType/handleServiceTypeConfigs';
import {BookingTypeEnum} from 'presentation/pages/private/booking/constants';
import {
	EMERGENCY_TELEHEALTH_ERROR_STATE,
	EMERGENCY_TELEHEALTH_EXIT_MODAL,
	EMERGENCY_TELEHEALTH_STEPS,
	EMERGENCY_TELEHEALTH_STEP_LABELS,
} from './constants';
import {
	ActionWrapper,
	CenteredCard,
	ContentArea,
	ContentWrapper,
	ItemIconImage,
	ItemIconWrapper,
	ListCard,
	ListContainer,
	ListItemRow,
} from './styles';

const {emergencyTelehealth} = translations['pt-br'].privateFlow.services;

export function EmergencyTelehealthJourney() {
	const navigate = useNavigate();
	const {palette} = useTheme();
	const {isMobile} = useMediaQueries();
	const {currentPhone, currentEmail} = usePersonalData();
	const onOpenVoiceTranscriptionDrawer = useVoiceTranscriptionDrawerStore(state => state.onOpen);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const resetBookingFlowStep = useBookingFlowStepsStore(state => state.resetBookingStep);
	const {isEnabled: isUrgencyTeleTermsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_URGENCY_TELE_TERMS,
	);
	const {getLink, isLoading, isError, setIsError} = useGetEndpointUrl(emergencyTelehealth);
	const makeHealthEmergencyLink = useHealthEmergencyLinkFactory();
	const [activeStep, setActiveStep] = useState(0);
	const [isExitModalOpen, setIsExitModalOpen] = useState(false);

	const currentStep = useMemo(() => EMERGENCY_TELEHEALTH_STEPS[activeStep], [activeStep]);
	const listItems = currentStep.listItems ?? [];
	const hasListItems = listItems.length > 0;
	const isLastStep = activeStep === EMERGENCY_TELEHEALTH_STEPS.length - 1;

	useEffect(() => {
		document.getElementById('content-container')?.scrollTo({top: 0, behavior: 'auto'});
		window.scrollTo({top: 0, left: 0, behavior: 'auto'});
	}, [activeStep]);

	const handleBack = () => {
		if (activeStep === 0) {
			navigate(HomeEnum.HOME_PAGE);
			return;
		}

		setActiveStep(prev => prev - 1);
	};

	const handleOpenExitModal = () => {
		setIsExitModalOpen(true);
	};

	const handleConfirmExit = () => {
		setIsExitModalOpen(false);
		navigate(HomeEnum.HOME_PAGE);
	};

	const handleSuccess = () => {
		window.location.assign(HomeEnum.HOME_PAGE);
	};

	const handlePrimaryAction = async () => {
		if (!isLastStep) {
			setActiveStep(prev => prev + 1);
			return;
		}

		if (isUrgencyTeleTermsEnabled) {
			onOpenVoiceTranscriptionDrawer(
				null,
				TermsOriginEnum.EMERGENCY_TELEHEALTH,
				() => setIsError(true),
				handleSuccess,
			);
			return;
		}

		const success = await getLink(makeHealthEmergencyLink);
		if (success === null) return;

		if (!success) {
			setIsError(true);
			return;
		}

		handleSuccess();
	};

	const handleSecondaryAction = () => {
		if (currentStep.key === 'emergency') {
			window.open('tel:192', '_self');
			return;
		}

		if (currentStep.key === 'whenNotToUse') {
			resetBookingData();
			resetBookingFlowStep();

			const route = handleServiceTypeConfigs({
				currentSearchParams: new URLSearchParams(window.location.search),
				setBookingData,
				selectedServiceType: BookingTypeEnum.TELEHEALTH,
				phone: currentPhone,
				email: currentEmail,
			});

			navigate(route || TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
		}
	};

	const handleTryAgain = async () => {
		setIsError(false);
		await handlePrimaryAction();
	};

	return (
		<>
			<MainHeader
				title={emergencyTelehealth}
				dataTagId={{
					backButton: AppointmentBookingEnum.CLICK_VOLTAR_AGENDAMENTO,
					closeButton: AppointmentBookingEnum.CLICK_DESCARTAR_AGENDAMENTO,
				}}
				handleBack={handleBack}
				handleClose={handleOpenExitModal}
				variant="subRoute">
				<StyledGrid.Root px={isMobile ? 2 : 4} pb={3}>
					<StyledGrid.Item sm={12} md={12} lg={12}>
						{isMobile ? (
							<StepProgressBar
								mt={1}
								currentStep={activeStep + 1}
								totalSteps={EMERGENCY_TELEHEALTH_STEP_LABELS.length}
							/>
						) : (
							<CustomStepper
								dataTagId={AppointmentBookingEnum.NOME_STEP}
								activeStep={activeStep}
								stepLabels={EMERGENCY_TELEHEALTH_STEP_LABELS}
							/>
						)}
					</StyledGrid.Item>
				</StyledGrid.Root>
			</MainHeader>

			<ContentWrapper>
				<ContentArea>
					{isError && (
						<CenteredCard>
							<ContentState
								variant="info"
								title={EMERGENCY_TELEHEALTH_ERROR_STATE.title}
								description={EMERGENCY_TELEHEALTH_ERROR_STATE.description}
								padding={0}
								footer={
									<Button
										variant="outlined"
										onClick={handleTryAgain}
										endIcon={<Icon name="cached-default" />}>
										{EMERGENCY_TELEHEALTH_ERROR_STATE.buttonLabel}
									</Button>
								}
							/>
						</CenteredCard>
					)}

					{!isError && hasListItems && (
						<ListCard>
							<Typography variant="h4" color={palette.text.primary}>
								{currentStep.title}
							</Typography>
							<Typography mt={2} color={palette.text.primary}>
								{currentStep.description}
							</Typography>

							<ListContainer>
								{listItems.map(item => (
									<ListItemRow key={item.text}>
										<ItemIconWrapper
											$variant={item.variant}
											$hasCustomIcon={Boolean(item.iconSrc)}>
											{item.iconSrc ? (
												<ItemIconImage
													src={item.iconSrc}
													alt=""
													aria-hidden="true"
												/>
											) : (
												<Icon
													name={item.iconName}
													size={20}
													color={
														item.variant === 'error'
															? palette.error.main
															: palette.primary.main
													}
												/>
											)}
										</ItemIconWrapper>
										<Typography color={palette.text.primary}>
											{item.text}
										</Typography>
									</ListItemRow>
								))}
							</ListContainer>
						</ListCard>
					)}

					{!isError && !hasListItems && (
						<CenteredCard>
							<ContentState
								variant={currentStep.iconVariant === 'error' ? 'error' : 'primary'}
								customIconName={currentStep.iconName}
								title={currentStep.title}
								description={currentStep.description}
								padding={0}
							/>
						</CenteredCard>
					)}

					{!isError ? (
						<ActionWrapper $isListContent={hasListItems}>
							{isMobile ? (
								<Stack gap={1} width="100%">
									<Button
										variant="contained"
										size="large"
										fullWidth
										onClick={handlePrimaryAction}
										disabled={isLoading}>
										{currentStep.primaryActionLabel}
									</Button>
									{currentStep.secondaryActionLabel ? (
										<Button
											variant="outlined"
											size="large"
											fullWidth
											onClick={handleSecondaryAction}>
											{currentStep.secondaryActionLabel}
										</Button>
									) : null}
								</Stack>
							) : (
								<Stack
									direction="row"
									justifyContent="flex-end"
									gap={2}
									width="100%">
									{currentStep.secondaryActionLabel ? (
										<Button
											variant="outlined"
											size="large"
											onClick={handleSecondaryAction}>
											{currentStep.secondaryActionLabel}
										</Button>
									) : null}
									<Button
										variant="contained"
										size="large"
										onClick={handlePrimaryAction}
										disabled={isLoading}>
										{currentStep.primaryActionLabel}
									</Button>
								</Stack>
							)}
						</ActionWrapper>
					) : null}
				</ContentArea>

				<Box px={isMobile ? 0 : 4} mt="auto">
					<MainFooter />
				</Box>
			</ContentWrapper>

			<AlertModal
				open={isExitModalOpen}
				onClose={() => setIsExitModalOpen(false)}
				headerTitle={EMERGENCY_TELEHEALTH_EXIT_MODAL.headerTitle}
				title={EMERGENCY_TELEHEALTH_EXIT_MODAL.title}
				description={EMERGENCY_TELEHEALTH_EXIT_MODAL.description}
				variant="info"
				footerContent={
					<Stack width="100%" gap={1}>
						<Button
							variant="contained"
							fullWidth
							size="large"
							onClick={handleConfirmExit}>
							{EMERGENCY_TELEHEALTH_EXIT_MODAL.primaryButton}
						</Button>
						<Button
							variant="outlined"
							fullWidth
							size="large"
							onClick={() => setIsExitModalOpen(false)}>
							{EMERGENCY_TELEHEALTH_EXIT_MODAL.secondaryButton}
						</Button>
					</Stack>
				}
			/>

			<VoiceTranscriptionDrawer />
		</>
	);
}
